import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ShieldCheck, ArrowLeft, AlertTriangle, CheckCircle2, Circle, Plus, Rocket, FileText, PackageCheck } from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  fetchOnboarding, patchBusinessVerification, patchClientContact, patchContentApproval,
  addRequestedChange, patchRequestedChange, patchCommercial, patchDomain, patchContactRouting,
  patchTechnical, patchFinalApproval, addOnboardingOverride, goLive,
  patchHandoff, addPostHandoverRequest, patchPostHandoverRequest,
  createProposal, patchProposal, changeProposalStatus,
  createAgreement, changeAgreementStatus, patchAgreementLegalReview, fetchCommercialConfig,
} from "@/lib/api";
import { PLANS, REGIONS } from "@/data/content";
import { buildProposalContent, buildAgreementSections, AGREEMENT_SECTION_KEYS } from "@/data/commercialTemplates";

const PROPOSAL_STATUSES = ["draft", "ready", "sent", "accepted", "declined", "superseded"];
const AGREEMENT_STATUSES = ["draft", "ready", "sent", "superseded"];
const HANDOFF_STATUSES = ["not_started", "package_prepared", "package_verified", "delivered", "accepted_by_client"];
const HANDOFF_LABELS = {
  not_started: "Not started", package_prepared: "Package prepared", package_verified: "Package verified",
  delivered: "Delivered", accepted_by_client: "Accepted by client",
};
const POST_HANDOVER_TYPES = ["separate_quote", "move_to_maintenance", "move_to_responsibility", "move_to_priority"];
const POST_HANDOVER_TYPE_LABELS = {
  separate_quote: "Separate quote requested", move_to_maintenance: "Move to Maintenance Only",
  move_to_responsibility: "Move to Responsibility Plan", move_to_priority: "Move to Priority & Reputation",
};

const BUSINESS_VERIFICATION_LABELS = {
  business_name: "Business / trading name", legal_name: "Legal name", primary_contact_person: "Primary contact person",
  owner_representative: "Owner / authorized representative", phone: "Phone", email: "Email", address: "Business address",
  branches: "Branch information", facebook: "Facebook", instagram: "Instagram", other_social: "Other official social links",
  business_hours: "Business hours",
};

const CONTENT_APPROVAL_LABELS = {
  business_name: "Business name", hero_copy: "Hero copy", about_story: "About / business story", services: "Services",
  team_staff: "Team / staff", branches: "Branches", contact_details: "Contact details", images: "Images",
  reviews_testimonials: "Reviews / testimonials", portfolio: "Project / product / event portfolio", faq: "FAQ",
  legal_professional_info: "Legal / professional information",
};

const CONTENT_STATUS_LABEL = { pending: "Pending", approved: "Approved", changes_requested: "Changes Requested", not_applicable: "N/A" };
const CONTENT_STATUS_CLASS = {
  pending: "bg-slate-100 text-slate-600", approved: "bg-emerald-100 text-emerald-800",
  changes_requested: "bg-amber-100 text-amber-800", not_applicable: "bg-slate-50 text-slate-400",
};

const TECHNICAL_LABELS = {
  production_build: "Production build passes", domain_resolves: "Official domain resolves", ssl_active: "HTTPS/SSL active",
  desktop_qa: "Desktop QA complete", mobile_qa: "Mobile QA complete", navigation_works: "All navigation works",
  no_broken_images: "No broken images", no_broken_links: "No broken links", phone_links_correct: "Phone links correct",
  email_links_correct: "Email links correct", social_links_correct: "Social links correct",
  form_routing_verified: "Form routing verified", error_404_checked: "404 behavior checked",
  metadata_title_checked: "Metadata / title checked", favicon_logo_checked: "Favicon / logo checked",
  indexing_configured: "robots/indexing configured", sitemap_configured: "Sitemap configured",
  backup_configured: "Backup configured", analytics_configured: "Analytics configured",
};

const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm";
const sectionCls = "mt-6 rounded-xl border border-slate-200 bg-white p-5";

function Field({ label, children }) {
  return <div><label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">{label}</label>{children}</div>;
}

function SaveButton({ onClick, saving, label = "Save" }) {
  return <button type="button" disabled={saving} onClick={onClick} className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">{label}</button>;
}

export default function AdminOnboarding() {
  const { slug } = useParams();
  const { token, email, checking, isLoggedIn, revealAdminUi, logout } = useAdminAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");
  const [goLiveModal, setGoLiveModal] = useState(false);
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [newChange, setNewChange] = useState({ section: "", request_text: "" });
  const [newProposalPlan, setNewProposalPlan] = useState({ plan_id: "ownership", region: "ph" });
  const [newAgreementPlan, setNewAgreementPlan] = useState({ plan_id: "ownership", region: "ph" });
  const [expandedAgreement, setExpandedAgreement] = useState(null);
  const [newHandoverRequest, setNewHandoverRequest] = useState({ request_type: POST_HANDOVER_TYPES[0], note: "" });
  const [commercialConfig, setCommercialConfig] = useState(null);

  const load = () => {
    if (!token) return;
    fetchOnboarding(token, slug)
      .then(setData)
      .catch((err) => setError(err.response?.data?.detail || "Could not load the onboarding record."));
  };

  useEffect(() => {
    load();
    if (token) fetchCommercialConfig(token).then(setCommercialConfig).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, slug]);

  const runSave = async (fn, successNote = "Saved.") => {
    setSaving(true);
    setNote("");
    try {
      await fn();
      setNote(successNote);
      load();
    } catch (err) {
      setNote(err.response?.data?.detail?.message || err.response?.data?.detail || "Could not save.");
    } finally {
      setSaving(false);
      setTimeout(() => setNote(""), 3000);
    }
  };

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;
  if (error) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-rose-600">{error}</div>;
  if (!isLoggedIn) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-slate-500">DEBUG — Admin UI revealed, but this page needs a real login to load real data.</div>;
  if (!data) return <div className="min-h-screen bg-slate-50 px-4 py-16 text-center text-slate-500">Loading…</div>;

  const ob = data.onboarding;
  const canOnboard = data.status === "proposal_accepted";
  const requiredSections = 7;
  const BLOCKER_SECTION = {
    business_identity: "business_verification", contact_information: "business_verification",
    "commercial.agreement": "commercial", "commercial.payment": "commercial",
    "domain.connected": "domain", domain_ssl: "domain",
    contact_routing_safety: "contact_routing", final_approval: "final_approval",
  };
  const sectionOf = (key) => BLOCKER_SECTION[key] || (key.startsWith("content.") ? "content_approval" : key.startsWith("technical.") ? "technical" : key);
  const incompleteSections = new Set(data.blockers.filter((b) => !b.overridden).map((b) => sectionOf(b.key)));
  const sectionsComplete = requiredSections - incompleteSections.size;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-4xl">
        <Link to={`/admin/sites/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft size={14} /> Back to {data.business_name}</Link>
        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={18} /><span className="text-xs font-bold uppercase tracking-[0.16em]">Logged in as {email}</span></div>
          <button type="button" onClick={logout} className="text-sm text-slate-500 hover:text-slate-800">Log out</button>
        </div>
        <h1 className="mt-4 font-display text-2xl md:text-3xl font-bold">Client Onboarding &amp; Go Live</h1>
        <p className="mt-1 text-sm text-slate-500">{data.business_name} · Website status: {data.status}</p>

        {!canOnboard && (
          <div className="mt-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
            <strong>Client onboarding has not started.</strong> Website status must be Proposal Accepted before onboarding can be completed. You can still preview the checklist below.
          </div>
        )}

        {note && <p className="mt-4 text-sm text-emerald-700">{typeof note === "string" ? note : JSON.stringify(note)}</p>}

        {/* READINESS */}
        <section className={sectionCls}>
          <h2 className="font-semibold flex items-center gap-2">Go Live Readiness</h2>
          <p className="mt-1 text-sm text-slate-600">{sectionsComplete} of {requiredSections} required sections complete · {data.blocking_count} blocker{data.blocking_count === 1 ? "" : "s"} remaining</p>
          <ul className="mt-4 space-y-2">
            {data.blockers.map((b) => (
              <li key={b.key} className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${b.overridden ? "border-slate-200 bg-slate-50 text-slate-400" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                <span className="flex items-center gap-2">
                  <AlertTriangle size={14} className="shrink-0" /> {b.label}{b.overridden && " (overridden)"}
                </span>
                {b.overridable && !b.overridden && (
                  <button type="button" onClick={() => { setOverrideModal(b); setOverrideReason(""); }} className="shrink-0 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold hover:bg-slate-100">Override</button>
                )}
              </li>
            ))}
            {data.blockers.length === 0 && (
              <li className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"><CheckCircle2 size={14} /> No blockers — ready for Go Live.</li>
            )}
          </ul>
        </section>

        {/* 1. BUSINESS VERIFICATION */}
        <section className={sectionCls}>
          <h2 className="font-semibold">1. Client / Business Verification</h2>
          <div className="mt-4 space-y-3">
            {Object.entries(ob.business_verification).map(([key, item]) => (
              <div key={key} className="grid sm:grid-cols-[1fr_auto_auto_auto] gap-2 items-center border-b border-slate-100 pb-2">
                <div>
                  <p className="text-xs font-semibold text-slate-500">{BUSINESS_VERIFICATION_LABELS[key]}</p>
                  <input
                    defaultValue={item.value || ""}
                    onBlur={(e) => {
                      if (e.target.value !== (item.value || "")) {
                        runSave(() => patchBusinessVerification(token, slug, { [key]: { value: e.target.value, correction_reason: "Updated via onboarding" } }));
                      }
                    }}
                    className={inputCls}
                    placeholder="Not set"
                  />
                </div>
                <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" defaultChecked={item.applicable} onChange={(e) => runSave(() => patchBusinessVerification(token, slug, { [key]: { applicable: e.target.checked } }))} /> Applicable</label>
                <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" defaultChecked={item.research_verified} onChange={(e) => runSave(() => patchBusinessVerification(token, slug, { [key]: { research_verified: e.target.checked } }))} /> Research verified</label>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><input type="checkbox" defaultChecked={item.client_confirmed} onChange={(e) => runSave(() => patchBusinessVerification(token, slug, { [key]: { client_confirmed: e.target.checked } }))} /> Client confirmed</label>
              </div>
            ))}
          </div>

          <h3 className="mt-6 text-sm font-semibold">Authorized Client Contact</h3>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <Field label="Name"><input defaultValue={ob.client_contact.name || ""} onBlur={(e) => runSave(() => patchClientContact(token, slug, { name: e.target.value }))} className={inputCls} /></Field>
            <Field label="Role"><input defaultValue={ob.client_contact.role || ""} onBlur={(e) => runSave(() => patchClientContact(token, slug, { role: e.target.value }))} className={inputCls} /></Field>
            <Field label="Email"><input defaultValue={ob.client_contact.email || ""} onBlur={(e) => runSave(() => patchClientContact(token, slug, { email: e.target.value }))} className={inputCls} /></Field>
            <Field label="Phone"><input defaultValue={ob.client_contact.phone || ""} onBlur={(e) => runSave(() => patchClientContact(token, slug, { phone: e.target.value }))} className={inputCls} /></Field>
            <Field label="Authorization status">
              <select defaultValue={ob.client_contact.verified} onChange={(e) => runSave(() => patchClientContact(token, slug, { verified: e.target.value }))} className={inputCls}>
                <option value="unknown">Unknown</option><option value="confirmed">Confirmed</option>
              </select>
            </Field>
          </div>
        </section>

        {/* 2. CONTENT APPROVAL */}
        <section className={sectionCls}>
          <h2 className="font-semibold">2. Website Content Approval</h2>
          <div className="mt-4 space-y-2">
            {Object.entries(ob.content_approval).map(([key, item]) => (
              <div key={key} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2">
                <span className="text-sm">{CONTENT_APPROVAL_LABELS[key]}</span>
                <select
                  defaultValue={item.status}
                  onChange={(e) => runSave(() => patchContentApproval(token, slug, { [key]: { status: e.target.value } }))}
                  className={`rounded-lg border border-slate-300 px-2 py-1 text-xs font-semibold ${CONTENT_STATUS_CLASS[item.status]}`}
                >
                  {Object.entries(CONTENT_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>

          <h3 className="mt-6 text-sm font-semibold">Client-Requested Changes</h3>
          <ul className="mt-3 space-y-2">
            {ob.requested_changes.map((c) => (
              <li key={c.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{c.section}</span>
                  <select
                    defaultValue={c.status}
                    onChange={(e) => runSave(() => patchRequestedChange(token, slug, c.id, { status: e.target.value }))}
                    className={`rounded-lg border px-2 py-1 text-xs font-semibold ${c.status === "completed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}
                  >
                    <option value="pending">Pending</option><option value="completed">Completed</option>
                  </select>
                </div>
                <p className="mt-1 text-slate-600">{c.request_text}</p>
                {c.admin_note && <p className="mt-1 text-xs text-slate-400">Note: {c.admin_note}</p>}
              </li>
            ))}
            {ob.requested_changes.length === 0 && <p className="text-sm text-slate-400">No requested changes recorded.</p>}
          </ul>
          <div className="mt-3 grid sm:grid-cols-[1fr_2fr_auto] gap-2">
            <input value={newChange.section} onChange={(e) => setNewChange({ ...newChange, section: e.target.value })} placeholder="Section (e.g. Services)" className={inputCls} />
            <input value={newChange.request_text} onChange={(e) => setNewChange({ ...newChange, request_text: e.target.value })} placeholder="Client's exact request" className={inputCls} />
            <button
              type="button"
              disabled={saving || !newChange.section || !newChange.request_text}
              onClick={() => runSave(async () => { await addRequestedChange(token, slug, newChange); setNewChange({ section: "", request_text: "" }); }, "Requested change added.")}
              className="inline-flex items-center justify-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
            ><Plus size={13} /> Add</button>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">This tracks requests only — an admin/developer still makes the actual approved website change by hand.</p>
        </section>

        {/* 3. COMMERCIAL */}
        <section className={sectionCls}>
          <h2 className="font-semibold">3. Commercial Setup</h2>
          <p className="mt-1 text-xs text-slate-500">Selected plan: <strong>{data.selected_plan || "not selected"}</strong> (from Website Management — reused here, not duplicated).</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Agreement status">
              <select defaultValue={ob.commercial.agreement_status} onChange={(e) => runSave(() => patchCommercial(token, slug, { agreement_status: e.target.value }))} className={inputCls}>
                {["not_started", "sent", "reviewing", "accepted", "declined"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Agreement reference"><input defaultValue={ob.commercial.agreement_reference || ""} onBlur={(e) => runSave(() => patchCommercial(token, slug, { agreement_reference: e.target.value }))} className={inputCls} /></Field>
            <Field label="Agreement version accepted"><input defaultValue={ob.commercial.agreement_version || ""} onBlur={(e) => runSave(() => patchCommercial(token, slug, { agreement_version: e.target.value }))} className={inputCls} placeholder="e.g. MSC-SA-slug-v1" /></Field>
            <Field label="Accepted by (client)"><input defaultValue={ob.commercial.agreement_accepted_by || ""} onBlur={(e) => runSave(() => patchCommercial(token, slug, { agreement_accepted_by: e.target.value }))} className={inputCls} /></Field>
            <Field label="Payment status">
              <select defaultValue={ob.commercial.payment_status} onChange={(e) => runSave(() => patchCommercial(token, slug, { payment_status: e.target.value }))} className={inputCls}>
                {["not_required_yet", "pending", "partially_paid", "paid", "failed", "refunded", "waived"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Payment reference"><input defaultValue={ob.commercial.payment_reference || ""} onBlur={(e) => runSave(() => patchCommercial(token, slug, { payment_reference: e.target.value }))} className={inputCls} placeholder="Invoice #, GCash ref, bank ref…" /></Field>
            <Field label="Payment method">
              <select defaultValue={ob.commercial.payment_method || ""} onChange={(e) => runSave(() => patchCommercial(token, slug, { payment_method: e.target.value || null }))} className={inputCls}>
                <option value="">Not set</option>
                {["bank_transfer", "gcash", "paymongo", "other"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Amount expected"><input defaultValue={ob.commercial.amount_expected || ""} onBlur={(e) => runSave(() => patchCommercial(token, slug, { amount_expected: e.target.value }))} className={inputCls} placeholder="From the plan's price, e.g. ₱42,000 one-time" /></Field>
            <Field label="Amount received"><input defaultValue={ob.commercial.amount_received || ""} onBlur={(e) => runSave(() => patchCommercial(token, slug, { amount_received: e.target.value }))} className={inputCls} /></Field>
          </div>
          <Field label="Payment note"><textarea defaultValue={ob.commercial.payment_note || ""} onBlur={(e) => runSave(() => patchCommercial(token, slug, { payment_note: e.target.value }))} rows={2} className={`${inputCls} mt-3`} /></Field>
          {ob.commercial.payment_received_at && <p className="mt-2 text-[11px] text-slate-400">Payment recorded received: {new Date(ob.commercial.payment_received_at).toLocaleString()}</p>}
          <p className="mt-3 text-[11px] text-slate-400">No card, bank, PayMongo, or other financial credentials are ever stored here — amounts and references are recorded manually, for tracking only. Bank Transfer and GCash are recorded manually; PayMongo covers online card/other payments once a merchant account is connected (not yet integrated — this only tracks that a PayMongo payment was recorded).</p>
        </section>

        {/* PROPOSALS */}
        <section className={sectionCls}>
          <h2 className="font-semibold flex items-center gap-2"><FileText size={16} /> Proposals</h2>
          <p className="mt-1 text-xs text-slate-500">Generated from the live Plans pricing/scope — plan and price are never freehand-editable once created; a changed plan means a new version.</p>
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <Field label="Plan">
              <select value={newProposalPlan.plan_id} onChange={(e) => setNewProposalPlan({ ...newProposalPlan, plan_id: e.target.value })} className={inputCls}>
                {PLANS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Region">
              <select value={newProposalPlan.region} onChange={(e) => setNewProposalPlan({ ...newProposalPlan, region: e.target.value })} className={inputCls}>
                {Object.values(REGIONS).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </Field>
            <button
              type="button"
              disabled={saving}
              onClick={() => runSave(async () => {
                const content = buildProposalContent(newProposalPlan.plan_id, newProposalPlan.region);
                await createProposal(token, { site_slug: slug, ...content });
              }, "Proposal generated.")}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
            >Generate Proposal</button>
          </div>
          <ul className="mt-4 space-y-2">
            {(data.proposals || []).map((p) => (
              <li key={p.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span><strong>v{p.version}</strong> — {p.proposal_title} <span className="text-slate-400">({p.price_display}{p.price_period ? ` ${p.price_period}` : ""})</span></span>
                  <select
                    defaultValue={p.status}
                    onChange={(e) => runSave(() => changeProposalStatus(token, p.id, e.target.value))}
                    className={`rounded-lg border px-2 py-1 text-xs font-semibold ${p.status === "accepted" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : p.status === "declined" || p.status === "superseded" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}
                  >
                    {PROPOSAL_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <textarea
                  defaultValue={p.summary || ""}
                  placeholder="Summary shown to the client (editable)…"
                  onBlur={(e) => { if (e.target.value !== (p.summary || "")) runSave(() => patchProposal(token, p.id, { summary: e.target.value })); }}
                  rows={2}
                  className={`${inputCls} mt-2`}
                />
              </li>
            ))}
            {(data.proposals || []).length === 0 && <p className="text-sm text-slate-400">No proposals generated yet.</p>}
          </ul>
        </section>

        {/* AGREEMENTS */}
        <section className={sectionCls}>
          <h2 className="font-semibold flex items-center gap-2"><FileText size={16} /> Service Agreements</h2>
          <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-800">DRAFT — NOT LEGALLY REVIEWED. Do not present as final/binding to a real client until reviewed by a qualified lawyer.</p>
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <Field label="Plan">
              <select value={newAgreementPlan.plan_id} onChange={(e) => setNewAgreementPlan({ ...newAgreementPlan, plan_id: e.target.value })} className={inputCls}>
                {PLANS.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </Field>
            <Field label="Region">
              <select value={newAgreementPlan.region} onChange={(e) => setNewAgreementPlan({ ...newAgreementPlan, region: e.target.value })} className={inputCls}>
                {Object.values(REGIONS).map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </Field>
            <button
              type="button"
              disabled={saving}
              onClick={() => runSave(async () => {
                const content = buildProposalContent(newAgreementPlan.plan_id, newAgreementPlan.region);
                const sections = buildAgreementSections(newAgreementPlan.plan_id, newAgreementPlan.region, commercialConfig?.payment_methods_enabled || [], commercialConfig?.paymongo_enabled_submethods || []);
                await createAgreement(token, {
                  site_slug: slug, plan_id: newAgreementPlan.plan_id, region: newAgreementPlan.region,
                  price_display: content.price_display, price_period: content.price_period,
                  scope_included: content.scope_included, scope_excluded: content.scope_excluded, sections,
                });
              }, "Agreement generated.")}
              className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
            >Generate Agreement</button>
          </div>
          <ul className="mt-4 space-y-2">
            {(data.agreements || []).map((a) => (
              <li key={a.id} className="rounded-lg border border-slate-200 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span><strong>{a.reference}</strong> <span className="text-slate-400">({a.price_display}{a.price_period ? ` ${a.price_period}` : ""})</span></span>
                  <select
                    defaultValue={a.status}
                    onChange={(e) => runSave(() => changeAgreementStatus(token, a.id, e.target.value))}
                    className={`rounded-lg border px-2 py-1 text-xs font-semibold ${a.status === "superseded" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}
                  >
                    {AGREEMENT_STATUSES.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className={`rounded-lg border px-2 py-1 text-xs font-semibold ${a.legal_review_status === "approved" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}>
                    Legal review: {a.legal_review_status || "pending"}
                  </span>
                  {a.legal_review_status !== "approved" ? (
                    <>
                      <span className="text-[11px] text-rose-600">Cannot be marked ready/sent until approved.</span>
                      <button type="button" onClick={() => runSave(() => patchAgreementLegalReview(token, a.id, "approved"))} className="text-[11px] font-semibold text-slate-500 underline hover:text-slate-800">Mark approved (only after real legal review)</button>
                    </>
                  ) : (
                    <button type="button" onClick={() => runSave(() => patchAgreementLegalReview(token, a.id, "pending"))} className="text-[11px] font-semibold text-slate-500 underline hover:text-slate-800">Revert to pending</button>
                  )}
                </div>
                <button type="button" onClick={() => setExpandedAgreement(expandedAgreement === a.id ? null : a.id)} className="mt-2 text-xs font-semibold text-slate-500 hover:text-slate-800">
                  {expandedAgreement === a.id ? "Hide sections" : "View all 18 sections"}
                </button>
                {expandedAgreement === a.id && (
                  <div className="mt-2 space-y-2 rounded-lg bg-slate-50 p-3">
                    {AGREEMENT_SECTION_KEYS.map((key) => (
                      <div key={key}>
                        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{key.replace(/_/g, " ")}</p>
                        <p className="text-xs text-slate-700">{a.sections?.[key]}</p>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
            {(data.agreements || []).length === 0 && <p className="text-sm text-slate-400">No agreements generated yet.</p>}
          </ul>
        </section>

        {/* WEBSITE OWNERSHIP HANDOFF */}
        <section className={sectionCls}>
          <h2 className="font-semibold flex items-center gap-2"><PackageCheck size={16} /> Website Ownership Handoff</h2>
          <p className="mt-1 text-xs text-slate-500">Independent of Go Live above — relevant once a Website Ownership sale is complete and paid. Never required for a subscription plan.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Handoff status">
              <select defaultValue={ob.handoff.status} onChange={(e) => runSave(() => patchHandoff(token, slug, { status: e.target.value }))} className={inputCls}>
                {HANDOFF_STATUSES.map((v) => <option key={v} value={v}>{HANDOFF_LABELS[v]}</option>)}
              </select>
            </Field>
            <Field label="Package version"><input defaultValue={ob.handoff.package_version || ""} onBlur={(e) => runSave(() => patchHandoff(token, slug, { package_version: e.target.value }))} className={inputCls} placeholder="e.g. v1 export" /></Field>
          </div>
          <Field label="Handoff notes"><textarea defaultValue={ob.handoff.notes || ""} onBlur={(e) => runSave(() => patchHandoff(token, slug, { notes: e.target.value }))} rows={2} className={`${inputCls} mt-3`} /></Field>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-slate-400">
            {ob.handoff.prepared_at && <span>Prepared: {new Date(ob.handoff.prepared_at).toLocaleString()}</span>}
            {ob.handoff.verified_at && <span>Verified: {new Date(ob.handoff.verified_at).toLocaleString()}</span>}
            {ob.handoff.delivered_at && <span>Delivered: {new Date(ob.handoff.delivered_at).toLocaleString()}</span>}
          </div>

          <h3 className="mt-6 text-sm font-semibold">Post-Handover Requests</h3>
          <p className="mt-1 text-xs text-slate-500">What an Ownership client can ask for afterward — always a new, separately handled item, never an automatic re-opening of this Agreement.</p>
          <ul className="mt-3 space-y-2">
            {(ob.post_handover_requests || []).map((req) => (
              <li key={req.id} className="rounded-lg border border-slate-100 p-3 text-sm">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold">{POST_HANDOVER_TYPE_LABELS[req.request_type]}</span>
                  <select
                    defaultValue={req.status}
                    onChange={(e) => runSave(() => patchPostHandoverRequest(token, slug, req.id, { status: e.target.value, resolution_note: req.resolution_note }))}
                    className={`rounded-lg border px-2 py-1 text-xs font-semibold ${req.status === "completed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : req.status === "declined" ? "bg-slate-100 text-slate-500 border-slate-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}
                  >
                    {["open", "in_progress", "completed", "declined"].map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                {req.note && <p className="mt-1 text-slate-600">{req.note}</p>}
                {req.resolution_note && <p className="mt-1 text-xs text-slate-400">Resolution: {req.resolution_note}</p>}
              </li>
            ))}
            {(ob.post_handover_requests || []).length === 0 && <p className="text-sm text-slate-400">No post-handover requests recorded.</p>}
          </ul>
          <div className="mt-3 grid sm:grid-cols-[1fr_2fr_auto] gap-2">
            <select value={newHandoverRequest.request_type} onChange={(e) => setNewHandoverRequest({ ...newHandoverRequest, request_type: e.target.value })} className={inputCls}>
              {POST_HANDOVER_TYPES.map((v) => <option key={v} value={v}>{POST_HANDOVER_TYPE_LABELS[v]}</option>)}
            </select>
            <input value={newHandoverRequest.note} onChange={(e) => setNewHandoverRequest({ ...newHandoverRequest, note: e.target.value })} placeholder="Note (optional)" className={inputCls} />
            <button
              type="button"
              disabled={saving}
              onClick={() => runSave(async () => { await addPostHandoverRequest(token, slug, newHandoverRequest); setNewHandoverRequest({ request_type: POST_HANDOVER_TYPES[0], note: "" }); }, "Request added.")}
              className="inline-flex items-center justify-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
            ><Plus size={13} /> Add</button>
          </div>
        </section>

        {/* 4. DOMAIN */}
        <section className={sectionCls}>
          <h2 className="font-semibold">4. Domain &amp; Identity</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Domain status">
              <select defaultValue={ob.domain.status} onChange={(e) => runSave(() => patchDomain(token, slug, { status: e.target.value }))} className={inputCls}>
                {["not_decided", "client_existing_domain", "new_domain_needed", "domain_ready", "dns_pending", "connected"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <Field label="Official domain"><input defaultValue={ob.domain.domain || ""} onBlur={(e) => runSave(() => patchDomain(token, slug, { domain: e.target.value }))} className={inputCls} placeholder="example.com" /></Field>
            <Field label="Domain owner">
              <select defaultValue={ob.domain.owner} onChange={(e) => runSave(() => patchDomain(token, slug, { owner: e.target.value }))} className={inputCls}>
                <option value="unknown">Unknown</option><option value="client">Client</option><option value="mysitecare_managed">MySiteCare-managed</option>
              </select>
            </Field>
            <Field label="Registrar / provider"><input defaultValue={ob.domain.provider || ""} onBlur={(e) => runSave(() => patchDomain(token, slug, { provider: e.target.value }))} className={inputCls} /></Field>
            <Field label="SSL status">
              <select defaultValue={ob.domain.ssl_status} onChange={(e) => runSave(() => patchDomain(token, slug, { ssl_status: e.target.value }))} className={inputCls}>
                {["not_configured", "pending", "active", "error"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700 mt-6"><input type="checkbox" defaultChecked={ob.domain.dns_access_confirmed} onChange={(e) => runSave(() => patchDomain(token, slug, { dns_access_confirmed: e.target.checked }))} /> DNS access confirmed</label>
          </div>
          <p className="mt-3 text-[11px] text-slate-400">Never store registrar/hosting/email passwords here — this tracks status only.</p>
        </section>

        {/* 5. CONTACT ROUTING */}
        <section className={sectionCls}>
          <h2 className="font-semibold">5. Contact &amp; Inquiry Routing</h2>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Public contact email"><input defaultValue={ob.contact_routing.public_contact_email || ""} onBlur={(e) => runSave(() => patchContactRouting(token, slug, { public_contact_email: e.target.value }))} className={inputCls} /></Field>
            <Field label="Public phone"><input defaultValue={ob.contact_routing.public_phone || ""} onBlur={(e) => runSave(() => patchContactRouting(token, slug, { public_phone: e.target.value }))} className={inputCls} /></Field>
            <Field label="Form recipient email"><input defaultValue={ob.contact_routing.form_recipient_email || ""} onBlur={(e) => runSave(() => patchContactRouting(token, slug, { form_recipient_email: e.target.value }))} className={inputCls} /></Field>
            <Field label="Form mode">
              <select defaultValue={ob.contact_routing.form_mode} onChange={(e) => runSave(() => patchContactRouting(token, slug, { form_mode: e.target.value }))} className={inputCls}>
                <option value="concept_only">Concept-only (default)</option><option value="disabled">Disabled — no form</option><option value="live">Live</option>
              </select>
            </Field>
          </div>
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" defaultChecked={ob.contact_routing.form_delivery_verified} onChange={(e) => runSave(() => patchContactRouting(token, slug, { form_delivery_verified: e.target.checked }))} /> Form delivery verified (tested manually)</label>
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-700"><input type="checkbox" defaultChecked={ob.contact_routing.visitor_ack_enabled} onChange={(e) => runSave(() => patchContactRouting(token, slug, { visitor_ack_enabled: e.target.checked }))} /> Send a business-branded receipt acknowledgment to the visitor</label>
          <p className="mt-3 text-[11px] text-slate-400">Website status alone never makes a form live — mode stays "Concept-only" until explicitly changed here.</p>
        </section>

        {/* 6. TECHNICAL */}
        <section className={sectionCls}>
          <h2 className="font-semibold">6. Technical Go-Live Checklist</h2>
          <div className="mt-4 space-y-2">
            {Object.entries(ob.technical).map(([key, item]) => (
              <div key={key} className="grid sm:grid-cols-[1fr_auto_auto] gap-2 items-center border-b border-slate-100 pb-2">
                <span className="text-sm flex items-center gap-2">{item.passed ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Circle size={14} className="text-slate-300" />} {TECHNICAL_LABELS[key]}</span>
                <select defaultValue={item.requirement} onChange={(e) => runSave(() => patchTechnical(token, slug, { [key]: { requirement: e.target.value } }))} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">
                  <option value="required">Required</option><option value="optional">Optional</option><option value="not_applicable">N/A</option>
                </select>
                <label className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><input type="checkbox" defaultChecked={item.passed} onChange={(e) => runSave(() => patchTechnical(token, slug, { [key]: { passed: e.target.checked } }))} /> Passed</label>
              </div>
            ))}
          </div>
          <Field label="Indexing status">
            <select defaultValue={ob.indexing_status} onChange={(e) => runSave(() => patchTechnical(token, slug, {}, e.target.value))} className={`${inputCls} mt-3 max-w-xs`}>
              <option value="concept_noindex">Concept — noindex</option>
              <option value="ready_for_indexing">Ready for indexing</option>
              <option value="indexing_enabled">Indexing enabled</option>
            </select>
          </Field>
        </section>

        {/* 7. FINAL APPROVAL */}
        <section className={sectionCls}>
          <h2 className="font-semibold">7. Final Client Approval</h2>
          <p className="mt-1 text-xs text-slate-500">Someone authorized by the client must genuinely approve the finished website — never inferred from payment, agreement, or sales stage.</p>
          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <Field label="Approved by"><input defaultValue={ob.final_approval.approved_by || ""} onBlur={(e) => runSave(() => patchFinalApproval(token, slug, { status: ob.final_approval.status, approved_by: e.target.value }))} className={inputCls} /></Field>
            <Field label="Role"><input defaultValue={ob.final_approval.approved_role || ""} onBlur={(e) => runSave(() => patchFinalApproval(token, slug, { status: ob.final_approval.status, approved_by: ob.final_approval.approved_by, approved_role: e.target.value }))} className={inputCls} /></Field>
          </div>
          <Field label="Note"><textarea defaultValue={ob.final_approval.note || ""} onBlur={(e) => runSave(() => patchFinalApproval(token, slug, { status: ob.final_approval.status, approved_by: ob.final_approval.approved_by, note: e.target.value }))} rows={2} className={`${inputCls} mt-3`} /></Field>
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm">Status: <strong>{ob.final_approval.status}</strong></span>
            {ob.final_approval.status !== "approved" && (
              <button
                type="button"
                onClick={() => runSave(() => patchFinalApproval(token, slug, { status: "approved", approved_by: ob.final_approval.approved_by, approved_role: ob.final_approval.approved_role, note: ob.final_approval.note }), "Final approval recorded.")}
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
              >Mark Approved</button>
            )}
            {ob.final_approval.status === "approved" && (
              <button type="button" onClick={() => runSave(() => patchFinalApproval(token, slug, { status: "changes_requested" }))} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold hover:bg-slate-100">Mark Changes Requested</button>
            )}
          </div>
        </section>

        {/* 8. GO LIVE */}
        <section className={sectionCls}>
          <h2 className="font-semibold flex items-center gap-2"><Rocket size={16} /> 8. Go Live</h2>
          {!canOnboard ? (
            <p className="mt-3 text-sm text-slate-500">Website status must be Proposal Accepted before Go Live is available.</p>
          ) : data.ready ? (
            <>
              <p className="mt-3 text-sm text-emerald-700">All required items are complete. Ready to mark this website Online Client.</p>
              <button type="button" onClick={() => setGoLiveModal(true)} className="mt-4 rounded-lg bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700">Mark Website Online</button>
            </>
          ) : (
            <p className="mt-3 text-sm text-amber-700">Go Live blocked — {data.blocking_count} item{data.blocking_count === 1 ? "" : "s"} above still need attention.</p>
          )}
        </section>

        {/* OVERRIDE MODAL */}
        {overrideModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h3 className="font-semibold text-lg">Override: {overrideModal.label}</h3>
              <p className="mt-2 text-sm text-slate-600">This item will be marked resolved without actually completing it. A reason is required and permanently recorded.</p>
              <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} rows={3} className={`${inputCls} mt-3`} placeholder="e.g. Analytics deferred at client's request." />
              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={() => setOverrideModal(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
                <button
                  type="button"
                  disabled={!overrideReason.trim() || saving}
                  onClick={() => runSave(async () => { await addOnboardingOverride(token, slug, overrideModal.key, overrideReason.trim()); setOverrideModal(null); }, "Override recorded.")}
                  className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
                >Confirm Override</button>
              </div>
            </div>
          </div>
        )}

        {/* GO LIVE MODAL */}
        {goLiveModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
              <h3 className="font-semibold text-lg">Make this website an Online Client site?</h3>
              <p className="mt-2 text-sm text-slate-600">This will mark the website as the business's official client website. Confirm that the client has approved the final content, required technical checks have passed, and public contact behavior is ready.</p>
              <div className="mt-5 flex justify-end gap-3">
                <button type="button" onClick={() => setGoLiveModal(false)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600">Cancel</button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => runSave(async () => { await goLive(token, slug, true); setGoLiveModal(false); }, "Website is now live.")}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
                ><Rocket size={14} /> Go Live</button>
              </div>
            </div>
          </div>
        )}

        {/* HISTORY */}
        <section className={sectionCls}>
          <h2 className="font-semibold">Onboarding History</h2>
          <ul className="mt-3 space-y-1.5 max-h-96 overflow-y-auto">
            {data.history.map((h) => (
              <li key={h.id} className="text-xs text-slate-500">
                {new Date(h.at).toLocaleString()} — {h.admin_email} · <strong>{h.field}</strong>: "{String(h.previous_value)}" → "{String(h.new_value)}"{h.note ? ` (${h.note})` : ""}
              </li>
            ))}
            {data.history.length === 0 && <p className="text-sm text-slate-400">No onboarding activity recorded yet.</p>}
          </ul>
        </section>
      </div>
    </div>
  );
}
